import React, { useState, useEffect } from 'react';
import axios from 'axios';
import GetCookie from '../../utils/GetCookie';
import TrimContent from "../../utils/TrimContent";
import authNavigate from '../../globalComponents/AuthNav';
import { useNavigate } from 'react-router-dom';
import ConfirmationModal from '../../utils/ConfirmationModal';
import TextractorModelLibraryList from './TextractorModelLibraryList';

const ExtractionModelForm = ({ 
  editingModel, 
  selectedFabricId, 
  selectedFabricName,
  selectedDocIds, 
  onSuccess, 
  onError, 
  onCancel,
  onProcessingStart, 
  onProcessingEnd    
}) => {
  const [formData, setFormData] = useState({
    table_name: '',
    description: '',
    columns: []
  });

  const [newColumn, setNewColumn] = useState({
    name: '',
    datatype: 'TEXT',
    description: ''
  });

  const [datatypes, setDatatypes] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  //magic prompt state variables
  const [magicPrompt, setMagicPrompt] = useState("");
  const [showMagicConfirm, setShowMagicConfirm] = useState(false);
  const [isMagicGenerating, setIsMagicGenerating] = useState(false);


  const [isLibraryOpen, setIsLibraryOpen] = useState(false);
  const [showImportConfirm, setShowImportConfirm] = useState(false);
  const [modelToImport, setModelToImport] = useState(null);


  const navigate = useNavigate();

  const API_BASE_URL = '/pceapi/textractor';
  const headers = {
    Authorization: GetCookie('access_token')
  };

  
  useEffect(() => {
    if (editingModel) {
      const columns = editingModel.columns?.map(col => ({
        ...col,
        isEditing: false, // Track edit state for each column
        originalName: col.name // Track original name for updates
      })) || [];
      
      setFormData({
        table_name: editingModel.table_name || '',
        description: editingModel.description || '',
        columns: columns
      });
    }
  }, [editingModel]);



  // Fetch available datatypes
  useEffect(() => {
    fetchDatatypes();
  }, []);

  const fetchDatatypes = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/datatypes`, { headers });
      if (response.data.status === 'success') {
        setDatatypes(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching datatypes:', error);
    }
  };

  // Check for duplicate column names
  const isDuplicateColumnName = (name, excludeIndex = -1) => {
    return formData.columns.some((col, index) => 
      index !== excludeIndex && col.name.toLowerCase() === name.toLowerCase()
    );
  };

  // Handle column addition
  const handleAddColumn = () => {
    if (!newColumn.name.trim()) {
      onError('Column name is required');
      return;
    
    }
    if (!newColumn.description.trim()) {
      onError('Description name is required');
      return;
    
    }

    // Check for duplicate column names
    if (isDuplicateColumnName(newColumn.name)) {
      onError(`Column name '${newColumn.name}' already exists`);
      return;
    }

    const updatedColumns = [...formData.columns, { 
      ...newColumn, 
      isEditing: false,
      originalName: newColumn.name, // For new columns, original = current
      isNew: true // Flag to identify new columns in the request
    }];
    
    setFormData({ ...formData, columns: updatedColumns });
    setNewColumn({
      name: '',
      datatype: 'TEXT',
      description: ''
    });
  };

  // Handle column removal
  const handleRemoveColumn = (index) => {
    const column = formData.columns[index];
    const updatedColumns = formData.columns.filter((_, i) => i !== index);
    
    // If it's an existing column (not new), mark it for deletion
    if (!column.isNew) {
      const existingColumn = { ...column, isDeleted: true };
      updatedColumns.push(existingColumn);
    }
    
    setFormData({ ...formData, columns: updatedColumns });
  };

  // Toggle edit mode for a column
  const toggleColumnEdit = (index) => {
    const updatedColumns = [...formData.columns];
    updatedColumns[index].isEditing = !updatedColumns[index].isEditing;
    setFormData({ ...formData, columns: updatedColumns });
  };

  // Save edited column
  const saveColumnEdit = (index) => {
    const column = formData.columns[index];
    const newName = column.name;

    // Check for duplicate names (excluding current column)
    if (isDuplicateColumnName(newName, index)) {
      onError(`Column name '${newName}' already exists`);
      return;
    }

    const updatedColumns = [...formData.columns];
    const updatedColumn = { ...column, isEditing: false };
    
    // Track if this column was modified
    if (!column.isNew && (
      column.name !== column.originalName || 
      column.datatype !== column.originalDatatype || 
      column.description !== column.originalDescription
    )) {
      updatedColumn.isModified = true;
    }
    
    updatedColumns[index] = updatedColumn;
    setFormData({ ...formData, columns: updatedColumns });
  };

  // Update column field
  const updateColumnField = (index, field, value) => {
    const updatedColumns = [...formData.columns];
    updatedColumns[index][field] = value;
    setFormData({ ...formData, columns: updatedColumns });
  };


  // Magic prompt handlers
  const handleMagicGenerate = () => {
    if (!magicPrompt.trim()) {
      onError('Please enter a magic prompt');
      return;
    }

    if (!selectedFabricId) {
      onError('Please select a project first');
      return;
    }

    const visibleColumns = formData.columns.filter(col => !col.isDeleted);
    if (visibleColumns.length > 0) {
      setShowMagicConfirm(true);
    } else {
      generateMagicColumns();
    }
  };

  const handleConfirmMagicGenerate = () => {
    setShowMagicConfirm(false);
    generateMagicColumns();
  };

  const generateMagicColumns = async () => {
    setIsMagicGenerating(true);

    if (onProcessingStart) {
      onProcessingStart('Your magic prompt is brewing your extraction model');
    }

    try {
      const response = await axios.post(
        `${API_BASE_URL}/generate-magic-model`,
        {
          fabric_id: selectedFabricId,
          magic_prompt: magicPrompt,
          model_description: formData.description || null
        },
        { headers }
      );

      if (response.data.status === 'success') {
        const generatedColumns = response.data.data.columns.map(col => ({
          ...col,
          isEditing: false,
          originalName: col.name,
          isNew: true
        }));

        setFormData(prev => ({
          ...prev,
          columns: generatedColumns
        }));
        setMagicPrompt('');
      } else {
        onError(response.data.message || 'Failed to generate columns');
      }
    } catch (error) {
      onError(error.response?.data?.message || 'Failed to generate columns. Please try again.');
    } finally {
      setIsMagicGenerating(false);
      if (onProcessingEnd) {
        onProcessingEnd();
      }
    }
  };

  const getMagicConfirmBodyContent = () => {
    const visibleColumns = formData.columns.filter(col => !col.isDeleted);
    return (
      <div>
        <div className="alert alert-warning" role="alert">
          <i className="bi bi-exclamation-triangle me-2"></i>
          <strong>Warning:</strong> You have existing columns in your extraction sheet.
        </div>
        <p>Generating a magic sheet will <strong>replace all</strong> your current columns...</p>
        <div className="mb-3 p-2 bg-light rounded">
          <strong>Prompt:</strong> "{magicPrompt}"
        </div>
        <p className="mb-2"><strong>Current sheet contains:</strong></p>
        <ul className="mb-3">
          <li>{visibleColumns.length} column(s)</li>
        </ul>
        <p className="text-danger mb-0">This action cannot be undone. All your current columns will be replaced.</p>
      </div>
    );
  };

  const performImport = ({ name, columns: columnsToImport }) => {
    const importedColumns = columnsToImport.map(col => ({
      ...col,
      isEditing: false,
      originalName: col.name,
      isNew: true
    }));

    setFormData(prev => ({
      ...prev,
      table_name: name,
      columns: importedColumns
    }));

    setIsLibraryOpen(false);
  };

  const handleModelImport = (importedData) => {
    const visibleColumns = formData.columns.filter(col => !col.isDeleted);
    if (visibleColumns.length === 0) {
      performImport(importedData);
    } else {
      setModelToImport(importedData);
      setShowImportConfirm(true);
    }
  };

  const handleConfirmImport = () => {
    if (modelToImport) {
      performImport(modelToImport);
    }
    setShowImportConfirm(false);
    setModelToImport(null);
  };

  const getImportConfirmBodyContent = () => (
    <div>
      <div className="alert alert-warning" role="alert">
        <i className="bi bi-exclamation-triangle me-2"></i>
        <strong>Warning:</strong> You have existing columns in your sheet.
      </div>
      <p>Importing will <strong>replace all</strong> your current columns.</p>
      <p className="text-danger mb-0">This action cannot be undone.</p>
    </div>
  );


  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.table_name.trim()) {
      onError('Model name is required');
      return;
    }

    const visibleColumns = formData.columns.filter(col => !col.isDeleted);
    if (visibleColumns.length === 0) {
      onError('At least one column is required');
      return;
    }

    if (selectedDocIds.length === 0) {
      onError('Please select documents for extraction');
      return;
    }

    setIsLoading(true);
    
    // Start processing popup
    const processingMessage = editingModel ? 'Updating extraction model' : 'Creating extraction model';
    if (onProcessingStart) {
      onProcessingStart(processingMessage);
    }

    try {
      const endpoint = editingModel ? '/update-model' : '/create-model';
      
      // Separate columns by their state for better handling
      let columns = visibleColumns;
      if (editingModel) {
        const newColumns = columns.filter(col => col.isNew);
        const modifiedColumns = columns.filter(col => col.isModified);
        const unchangedColumns = columns.filter(col => !col.isNew && !col.isModified);
        
        // Send structured data for updates
        columns = {
          new: newColumns,
          modified: modifiedColumns,
          unchanged: unchangedColumns
        };
      }

      const requestData = {
        table_name: formData.table_name,
        description: formData.description,
        columns: columns,
        doc_ids: selectedDocIds,
        fabric_id: selectedFabricId
      };

      if (editingModel) {
        requestData.table_id = editingModel.id;
      }

      const response = await axios.post(
        `${API_BASE_URL}${endpoint}`, 
        requestData, 
        { headers }
      );

      const result = response.data;

      if (result.status === 'NotAuthorized') {
        authNavigate('/login', 'iam', navigate);
        return;
      }

      if (result.status === 'failed') {
        onError(result.message);
        return;
      }

      onSuccess(result.message);
    } catch (error) {
      console.error('Error saving model:', error);
      onError(error.message || 'Failed to save model');
    } finally {
      setIsLoading(false);
      // End processing popup
      if (onProcessingEnd) {
        onProcessingEnd();
      }
    }
  };

  // Render column row (editable)
  const renderColumnRow = (column, index) => (
    <tr key={index}>
      {column.isEditing ? (
        <>
          <td className="align-top w-25">
            <input 
              type="text" 
              className="formField3 ev-btn-reg" 
              value={column.name}
              onChange={(e) => updateColumnField(index, 'name', e.target.value)}
              placeholder="Column name"
            />
          </td>
          <td className="align-top w-20">
            <select 
              className="formField3 ev-btn-reg customFormDropdown"
              value={column.datatype}
              onChange={(e) => updateColumnField(index, 'datatype', e.target.value)}
            >
              {datatypes.map((type, idx) => (
                <option key={idx} value={type}>{type}</option>
              ))}
            </select>
          </td>
          <td className="align-top w-35">
            <input 
              type="text" 
              className="form-control ev-btn-reg formField3" 
              value={column.description}
              onChange={(e) => updateColumnField(index, 'description', e.target.value)}
              placeholder="AI Description"
            />
          </td>
          <td className="align-top w-20">
            <button 
              className="ev-btn-blue-fill-round ev-btn-small ev-bold-reg" 
              onClick={() => saveColumnEdit(index)}
              type="button" 
            >
              <i className="bi bi-check-circle-fill"></i> Save
            </button>
            <button 
              className="ev-btn-white-fill-round ev-btn-small ev-bold-reg ms-1"
              onClick={() => toggleColumnEdit(index)}
              type="button" 
            >
              <i className="bi bi-x-circle-fill"></i> Cancel
            </button>
          </td>
        </>
      ) : (
        <>
          <td className="align-top w-25">
            <div className="ev-message-card5 ev-fs-11 avatar-tooltip" data-tooltip={column.name}>
             <TrimContent text={column.name} maxLength={25} />
            </div>
            </td>
          <td className="align-top w-20">{column.datatype}</td>
          <td className="align-top w-35">{column.description || '-'}</td>
          <td className="align-top w-20">
            <button 
             type="button"
              className="ev-btn-lightblue-fill-round ev-btn-small ev-bold-reg"
              onClick={() => toggleColumnEdit(index)}
              disabled={isLoading}
            >
              <i className="bi bi-pen-fill"></i> Edit
            </button>
            <button 
              type="button"
              className="ev-btn-lightblue-fill-round ev-btn-small ev-bold-reg ms-1"
              onClick={() => handleRemoveColumn(index)}
              disabled={isLoading}
            >
              <i className="bi bi-x-circle-fill"></i> Discard
            </button>
          </td>
        </>
      )}
    </tr>
  );

  return (
    <div className="ev-card-2 w-100 ev-padding-large dashboard-form w-100 mt-2">
      <p className="ev-med-p-blue mb-3">
        {editingModel ? "Edit Sheet" : "Create Sheet"}
        <span className="ev-message-card3 ms-3">
          <i className="bi bi-boxes"></i>&nbsp;{" "}
          <b>
            {selectedFabricName ? ` ${selectedFabricName}` : "Select Project"}
          </b>{" "}
          ({selectedDocIds.length} documents)
        </span>
      </p>

      <form onSubmit={handleSubmit}>
        <div className="row mb-2">
          <div className="col-6">
            <div className="form-group mb-2">
              <label className="ev-reg-p-dark mb-2">
                Model Name <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                className="formField3"
                value={formData.table_name}
                onChange={(e) =>
                  setFormData({ ...formData, table_name: e.target.value })
                }
                required
                placeholder="Enter model name"
                disabled={isLoading}
              />
            </div>
          </div>
          <div className="col-6">
            <div className="form-group mb-2">
              <label className="ev-reg-p-dark mb-2">
                Sheet Description{" "}
                <span className="ev-small-p-blue">
                  <i>(Optional)</i>
                </span>
              </label>
              <input
                type="text"
                className="formField3"
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                placeholder="Enter description"
                disabled={isLoading}
              />
            </div>
          </div>
        </div>

        {/* Magic Prompt */}
        <label className="ev-reg-p-dark mb-1">
          Magic Sheet Prompt{" "}
          <span className="ev-small-p-blue">
            <i>(Optional)</i>
          </span>
        </label>
        <div className="ev-card-8 mb-2 p-1 ev-radius-small">
          <div className="form-group mb-1">
            <input
              type="text"
              className="formField3"
              placeholder="Auto generate columns from a prompt"
              value={magicPrompt}
              onChange={(e) => setMagicPrompt(e.target.value)}
              disabled={isLoading || isMagicGenerating}
            />
          </div>
          <div className="text-end">
            <button
              type="button"
              className="ev-btn-gradient-fill-round avatar-tooltip ev-radius-xsmall ev-bold-reg ev-btn-small"
              onClick={handleMagicGenerate}
              data-tooltip={
                !selectedFabricId
                  ? "Please select a project"
                  : !magicPrompt.trim()
                    ? "Please enter a prompt"
                    : undefined
              }
              disabled={
                !selectedFabricId ||
                !magicPrompt.trim() ||
                isLoading ||
                isMagicGenerating
              }
            >
              {isMagicGenerating ? (
                <>Generating</>
              ) : (
                <>
                  <i className="bi bi-play-fill"></i> Generate
                </>
              )}
            </button>
          </div>
        </div>

        {/* Add column section */}
        <div className="d-flex align-items-center gap-3 mb-2">
          <label className="ev-reg-p-dark mb-0">
            Define Columns <span className="text-danger">*</span>
          </label>
          {!editingModel && (
            <div className="position-relative w-60">
              <button
                type="button"
                className="ev-btn-lightblue-fill-round ev-bold-reg"
                onClick={() => setIsLibraryOpen(!isLibraryOpen)}
              >
                <i className="bi bi-box-arrow-in-down"></i> Import Sheet
              </button>

              <TextractorModelLibraryList
                isOpen={isLibraryOpen}
                onClose={() => setIsLibraryOpen(false)}
                onImport={handleModelImport}
                onError={onError}
              />
            </div>
          )}
        </div>
        <div className="row mb-2 mt-2 ">
          <div className="col-3">
            <input
              type="text"
              className="formField3"
              value={newColumn.name}
              onChange={(e) =>
                setNewColumn({ ...newColumn, name: e.target.value })
              }
              placeholder="Column name"
              disabled={isLoading}
            />
          </div>
          <div className="col-3">
            <select
              className="formField3 customFormDropdown"
              value={newColumn.datatype}
              onChange={(e) =>
                setNewColumn({ ...newColumn, datatype: e.target.value })
              }
              disabled={isLoading}
            >
              {datatypes.map((type, index) => (
                <option key={index} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </div>
          <div className="col-4">
            <input
              type="text"
              className="formField3"
              value={newColumn.description}
              onChange={(e) =>
                setNewColumn({ ...newColumn, description: e.target.value })
              }
              placeholder="AI Description"
              disabled={isLoading}
            />
          </div>

          <div className="text-end col-2 d-flex align-items-center">
            <button
              type="button"
              className="ev-btn-blue-fill-round ev-btn-reg ev-bold-reg"
              onClick={handleAddColumn}
              disabled={isLoading}
            >
              <i className="bi bi-plus-circle"></i> Add Column
            </button>
          </div>
        </div>

            {/* Display columns table */}
            {formData.columns.filter(col => !col.isDeleted).length > 0 && (
              <div className="ev-card-4">
                <table className="table-custom1 mt-0 overflow-hidden w-100">
                  <thead>
                    <tr>
                      <th className="w-25">
                        <label>Column Name</label>
                      </th>
                      <th className="w-20">
                        <label>Datatype</label>
                      </th>
                      <th className="w-35">
                        <label>AI Description</label>
                      </th>
                      <th className="w-20">
                        <label>Actions</label>
                      </th>
                    </tr>
                  </thead>
                </table>

                <div className="scrollable-element vh-25">
                  <table className="table-custom1 overflow-hidden w-100">
                    <tbody>
                      {formData.columns
                        .filter(col => !col.isDeleted)
                        .map((column, index) => renderColumnRow(column, index))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
    
        
            
            <div className="mt-3 text-end">
              <button 
                type="submit" 
                className="ev-btn-blue-fill-round ev-bold-reg" 
                disabled={isLoading}
              >
                <i className="bi bi-cursor-fill"></i> {editingModel ? 'Update Sheet' : 'Create Sheet'}
              </button>
              
              <button 
                type="button" 
                className="ev-btn-blue-outline-round ev-bold-reg ms-2"
                onClick={onCancel}
                disabled={isLoading}
              >
                Cancel
              </button>
            </div>
    
      </form>

      {/* Magic Prompt Confirmation Modal */}
      <ConfirmationModal
        show={showMagicConfirm}
        onClose={() => setShowMagicConfirm(false)}
        onConfirm={handleConfirmMagicGenerate}
        title="Confirm Magic Model Generation"
        message=""
        confirmButtonText="Generate & Replace"
        confirmButtonClass="btn-warning"
        bodyContent={getMagicConfirmBodyContent()}
      />

      <ConfirmationModal
        show={showImportConfirm}
        onClose={() => setShowImportConfirm(false)}
        onConfirm={handleConfirmImport}
        title="Confirm Model Import"
        message=""
        confirmButtonText="Import & Replace"
        confirmButtonClass="btn-warning"
        bodyContent={getImportConfirmBodyContent()}
      />
    </div>
  );
};

export default ExtractionModelForm;